/**
 * Diesel Dispensary — Product Collection Navigation
 *
 * Collection handle resolution order:
 * 1. ?collection= URL param
 * 2. Referrer path /collections/<handle>/...
 * 3. Current URL path /collections/<handle>/...
 * 4. Referrer is /pages/all-products → "all"
 * 5. Universal fallback → "all"
 * 6. Product not found in resolved collection → retry with "all"
 */

(function () {
  'use strict';

  const NAV_SELECTOR = '.dd-col-nav';
  const WRAP_SELECTOR = '.dd-col-nav-wrap';

  // ── Relocate nav above main-product section ──────────────────────────────────
  function relocateNav() {
    const wrap = document.querySelector(WRAP_SELECTOR);
    if (!wrap) return;
    const productInfo = document.querySelector('product-info');
    if (!productInfo) { wrap.style.visibility = 'visible'; return; }
    const sectionEl = productInfo.closest('[id^="shopify-section-"]');
    if (!sectionEl) { wrap.style.visibility = 'visible'; return; }
    sectionEl.insertBefore(wrap, sectionEl.firstChild);
    wrap.style.visibility = 'visible';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', relocateNav);
  } else {
    relocateNav();
  }

  // ── Collection handle resolution ─────────────────────────────────────────────
  const nav = document.querySelector(NAV_SELECTOR);
  if (!nav) return;

  const params = new URLSearchParams(window.location.search);
  let collectionHandle = params.get('collection');

  if (!collectionHandle) {
    try {
      const ref = new URL(document.referrer);
      const refMatch = ref.pathname.match(/\/collections\/([^\/]+)/);
      if (refMatch) {
        collectionHandle = refMatch[1];
      } else if (
        ref.pathname.startsWith('/pages/all-products') ||
        ref.pathname === '/collections/all' ||
        ref.pathname.startsWith('/collections/all')
      ) {
        collectionHandle = 'all';
      }
    } catch(e) {}
  }

  if (!collectionHandle) {
    const pathMatch = window.location.pathname.match(/\/collections\/([^\/]+)/);
    if (pathMatch) collectionHandle = pathMatch[1];
  }

  // Universal fallback — always show the nav
  if (!collectionHandle) collectionHandle = 'all';

  const currentHandle = nav.dataset.productHandle;
  if (!currentHandle) return;

  // ── Fetch + build ────────────────────────────────────────────────────────────
  async function fetchCollectionProducts(handle) {
    const products = [];
    let page = 1;
    while (true) {
      const res = await fetch(`/collections/${handle}/products.json?limit=250&page=${page}`);
      if (!res.ok) break;
      const data = await res.json();
      if (!data.products || data.products.length === 0) break;
      products.push(...data.products);
      if (data.products.length < 250) break;
      page++;
    }
    return products;
  }

  async function buildNav(handle) {
    try {
      const products = await fetchCollectionProducts(handle);
      if (!products.length) { nav.style.display = 'none'; return; }

      let currentIndex = products.findIndex(p => p.handle === currentHandle);

      // Product not in this collection — fall back to "all"
      if (currentIndex === -1 && handle !== 'all') {
        return buildNav('all');
      }
      if (currentIndex === -1) { nav.style.display = 'none'; return; }

      const prevProduct = currentIndex > 0 ? products[currentIndex - 1] : null;
      const nextProduct = currentIndex < products.length - 1 ? products[currentIndex + 1] : null;
      const baseUrl = (p) => `/collections/${handle}/products/${p.handle}`;
      const prevImage = prevProduct?.images?.[0]?.src;
      const nextImage = nextProduct?.images?.[0]?.src;

      nav.innerHTML = `
        <div class="dd-col-nav__inner">
          <a href="${prevProduct ? baseUrl(prevProduct) : '#'}"
            class="dd-col-nav__btn dd-col-nav__btn--prev${!prevProduct ? ' dd-col-nav__btn--disabled' : ''}"
            aria-label="Previous product"
            ${!prevProduct ? 'tabindex="-1" aria-disabled="true"' : ''}>
            <svg class="dd-col-nav__arrow" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            ${prevProduct ? `
              ${prevImage ? `<img class="dd-col-nav__thumb" src="${prevImage}&width=56" alt="${prevProduct.title}" width="28" height="28" loading="lazy">` : ''}
              <span class="dd-col-nav__label">
                <span class="dd-col-nav__direction">Previous</span>
                <span class="dd-col-nav__title">${prevProduct.title}</span>
              </span>` : `<span class="dd-col-nav__label"><span class="dd-col-nav__direction">No previous</span></span>`}
          </a>

          <a href="/collections/${encodeURIComponent(handle)}"
            class="dd-col-nav__collection-link" aria-label="Back to collection">
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" width="14" height="14">
              <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            All
          </a>

          <a href="${nextProduct ? baseUrl(nextProduct) : '#'}"
            class="dd-col-nav__btn dd-col-nav__btn--next${!nextProduct ? ' dd-col-nav__btn--disabled' : ''}"
            aria-label="Next product"
            ${!nextProduct ? 'tabindex="-1" aria-disabled="true"' : ''}>
            ${nextProduct ? `
              <span class="dd-col-nav__label">
                <span class="dd-col-nav__direction">Next</span>
                <span class="dd-col-nav__title">${nextProduct.title}</span>
              </span>
              ${nextImage ? `<img class="dd-col-nav__thumb" src="${nextImage}&width=56" alt="${nextProduct.title}" width="28" height="28" loading="lazy">` : ''}` : `<span class="dd-col-nav__label"><span class="dd-col-nav__direction">No next</span></span>`}
            <svg class="dd-col-nav__arrow" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>
        <div class="dd-col-nav__progress" aria-hidden="true">
          <span>${currentIndex + 1} / ${products.length}</span>
        </div>
      `;
    } catch (e) {
      nav.style.display = 'none';
    }
  }

  buildNav(collectionHandle);
})();
